import React, { useCallback, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Customized,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DateRangePicker } from '@mui/x-date-pickers-pro';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetAdGroupBidHistory } from '~/hooks/apis/public-offer-feed/useAdGroupBidHistory';

import { getAdSpendHistory, getPidHistory } from '~/services/PublicOfferFeedService';
import { Dayjs, addDays, diffDays, formatDateISOUTC, toDayJs } from '~/services/TimeService';

function addDaysUtc(dateString: string, days: number): string {
  // Force UTC midnight to avoid timezone/DST shifts when working with YYYY-MM-DD strings.
  return formatDateISOUTC(addDays(days, `${dateString}T00:00:00.000Z`));
}

function diffDaysInclusiveUtc(startDate: string, endDate: string): number {
  // `diffDays` is exclusive (same day => 0); this chart needs inclusive day counts.
  const diff = diffDays(`${endDate}T00:00:00.000Z`, `${startDate}T00:00:00.000Z`, 'day');
  return Math.max(1, diff + 1);
}

type PidHistoryChartRow = {
  date: string;
  createdAt: string;
  pidKp: number | null;
  pidKi: number | null;
  pidKd: number | null;
  pidActualDailyAdSpend: number | null;
  targetDailyAdSpend: number | null;
  bqActualDailyAdSpend: number | null;
  bidHistoryTarget: number | null;
  bidHistoryController: string | null;
  actualNewTargetRoas: number | null;
  clampedNewTargetRoas: number | null;
};

type PidHistoryTooltipContentProps = {
  active?: boolean;
  payload?: Array<{ payload?: unknown }>;
  label?: unknown;
};

type ChartSeriesKey =
  | 'pidActualDailyAdSpend'
  | 'bqActualDailyAdSpend'
  | 'bidHistoryTarget'
  | 'targetDailyAdSpend'
  | 'actualNewTargetRoas'
  | 'clampedNewTargetRoas';

const CHART_SERIES: Array<{
  key: ChartSeriesKey;
  label: string;
  color: string;
  yAxisId: 'spend' | 'roas';
}> = [
  {
    key: 'bqActualDailyAdSpend',
    label: 'BQ - Actual daily ad spend',
    color: '#00897b',
    yAxisId: 'spend',
  },
  {
    key: 'bidHistoryTarget',
    label: 'Bid History - tROAS',
    color: '#6d4c41',
    yAxisId: 'roas',
  },
  {
    key: 'pidActualDailyAdSpend',
    label: 'PID - Actual daily ad spend',
    color: '#1976d2',
    yAxisId: 'spend',
  },
  {
    key: 'targetDailyAdSpend',
    label: 'PID - Target daily ad spend',
    color: '#2e7d32',
    yAxisId: 'spend',
  },
  {
    key: 'actualNewTargetRoas',
    label: 'PID - Actual new target ROAS',
    color: '#9c27b0',
    yAxisId: 'roas',
  },
  {
    key: 'clampedNewTargetRoas',
    label: 'PID - Clamped new target ROAS',
    color: '#ed6c02',
    yAxisId: 'roas',
  },
];

export default function AdgroupHistoryChart({
  customerId,
  campaignId,
  adGroupId,
}: {
  customerId: string;
  campaignId: string;
  adGroupId: string;
}) {
  const todayUtc = useMemo(() => formatDateISOUTC(new Date()), []);

  // The ad-spend-history endpoint only supports `days`, so the UI range is "ending at endDate".
  const [endDate, setEndDate] = useState<string>(todayUtc);
  const [startDate, setStartDate] = useState<string>(() => addDaysUtc(todayUtc, -29));
  const dateRange = useMemo((): [Dayjs, Dayjs] => [toDayJs(startDate), toDayJs(endDate)], [endDate, startDate]);

  const pidEndDate = useMemo(() => {
    // API expects endDate to be "selected endDate + 1" (inclusive-of-end style).
    return addDaysUtc(endDate, 1);
  }, [endDate]);

  const spendHistoryDays = useMemo(() => {
    return diffDaysInclusiveUtc(startDate, endDate);
  }, [endDate, startDate]);

  const {
    data: adSpendHistoryData = [],
    isLoading: isAdSpendHistoryLoading,
    error: adSpendHistoryError,
  } = useQuery({
    queryKey: ['googleAds', 'adSpendHistory', customerId, campaignId, adGroupId, spendHistoryDays],
    queryFn: async ({ signal }) => {
      const response = await getAdSpendHistory(
        { customerId, campaignId, adGroupId, days: spendHistoryDays },
        { signal },
      );
      const raw = (response?.result ?? []) as Array<{
        date?: string | { value?: string | null } | null;
        spend?: number | null;
      }>;

      return raw
        .map((row) => {
          const date = typeof row.date === 'string' ? row.date : (row.date?.value ?? null);
          if (!date) {
            return null;
          }
          return { date, bqActualDailyAdSpend: Number(row.spend ?? 0) };
        })
        .filter((row): row is { date: string; bqActualDailyAdSpend: number } => row !== null);
    },
    enabled: Boolean(customerId && campaignId && adGroupId),
  });

  const {
    data: pidHistoryData = [],
    isLoading: isPidHistoryLoading,
    error: pidHistoryError,
  } = useQuery({
    queryKey: ['googleAds', 'pidHistory', customerId, campaignId, adGroupId, startDate, pidEndDate],
    queryFn: async ({ signal }) => {
      const response = await getPidHistory(
        { customerId, campaignId, adGroupId, startDate, endDate: pidEndDate },
        { signal },
      );
      const raw = (response?.result ?? []) as Array<{
        createdAt?: string | null;
        pidKp?: number | null;
        pidKi?: number | null;
        pidKd?: number | null;
        targetDailyAdSpend?: number | null;
        actualDailyAdSpend?: number | null;
        actualNewTargetRoas?: number | null;
        clampedNewTargetRoas?: number | null;
      }>;

      return raw
        .map((row) => {
          const settingsDate = row.createdAt ? row.createdAt.slice(0, 10) : null;
          if (!settingsDate) {
            return null;
          }
          return {
            date: settingsDate,
            actualDailyAdSpendDate: addDaysUtc(settingsDate, -1),
            createdAt: row.createdAt ?? settingsDate,
            pidKp: Number(row.pidKp ?? 0),
            pidKi: Number(row.pidKi ?? 0),
            pidKd: Number(row.pidKd ?? 0),
            pidActualDailyAdSpend: Number(row.actualDailyAdSpend ?? 0),
            targetDailyAdSpend: Number(row.targetDailyAdSpend ?? 0),
            actualNewTargetRoas: row.actualNewTargetRoas === null ? null : Number(row.actualNewTargetRoas ?? null),
            clampedNewTargetRoas: row.clampedNewTargetRoas === null ? null : Number(row.clampedNewTargetRoas ?? null),
          };
        })
        .filter(
          (
            row,
          ): row is {
            date: string;
            actualDailyAdSpendDate: string;
            createdAt: string;
            pidKp: number;
            pidKi: number;
            pidKd: number;
            pidActualDailyAdSpend: number;
            targetDailyAdSpend: number;
            actualNewTargetRoas: number | null;
            clampedNewTargetRoas: number | null;
          } => row !== null,
        );
    },
    enabled: Boolean(customerId && campaignId && adGroupId),
  });

  const {
    data: bidHistoryResponse,
    isLoading: isBidHistoryLoading,
    error: bidHistoryError,
  } = useGetAdGroupBidHistory({
    customerId,
    campaignId,
    adGroupId,
    page: 0,
    limit: 500,
    enabled: Boolean(customerId && campaignId && adGroupId),
  });

  const bidHistoryTargetData = useMemo(() => {
    return (bidHistoryResponse?.result ?? [])
      .map((row) => {
        const updatedAt = row.updatedAt;
        const target = Number(row.target);
        if (!updatedAt || Number.isNaN(target)) {
          return null;
        }

        return {
          date: updatedAt,
          target,
          controller: row.biddingController ?? '-',
        };
      })
      .filter((row): row is { date: string; target: number; controller: string } => row !== null);
  }, [bidHistoryResponse]);

  const adSpendChartData = useMemo(() => {
    const rowsByDate = new Map<string, PidHistoryChartRow>();

    const getOrCreate = (date: string): PidHistoryChartRow => {
      const existing = rowsByDate.get(date);
      if (existing) return existing;
      const newRow: PidHistoryChartRow = {
        date,
        createdAt: date,
        pidKp: null,
        pidKi: null,
        pidKd: null,
        pidActualDailyAdSpend: null,
        targetDailyAdSpend: null,
        bqActualDailyAdSpend: null,
        bidHistoryTarget: null,
        bidHistoryController: null,
        actualNewTargetRoas: null,
        clampedNewTargetRoas: null,
      };
      rowsByDate.set(date, newRow);
      return newRow;
    };

    for (const row of pidHistoryData) {
      // Settings (pidKp/pidKi/pidKd, targetDailyAdSpend, ROAS) belong at the original createdAt date
      const settingsRow = getOrCreate(row.date);
      settingsRow.createdAt = row.createdAt;
      settingsRow.pidKp = row.pidKp;
      settingsRow.pidKi = row.pidKi;
      settingsRow.pidKd = row.pidKd;
      settingsRow.targetDailyAdSpend = row.targetDailyAdSpend;
      settingsRow.actualNewTargetRoas = row.actualNewTargetRoas;
      settingsRow.clampedNewTargetRoas = row.clampedNewTargetRoas;

      // Actual daily ad spend belongs at the offset date (1 day earlier)
      getOrCreate(row.actualDailyAdSpendDate).pidActualDailyAdSpend = row.pidActualDailyAdSpend;
    }

    for (const row of adSpendHistoryData) {
      getOrCreate(row.date).bqActualDailyAdSpend = row.bqActualDailyAdSpend;
    }

    for (const row of bidHistoryTargetData) {
      const historyRow = getOrCreate(row.date);
      historyRow.createdAt = row.date;
      historyRow.bidHistoryTarget = row.target;
      historyRow.bidHistoryController = row.controller;
    }

    return [...rowsByDate.values()].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [adSpendHistoryData, bidHistoryTargetData, pidHistoryData]);

  const chartRowByDate = useMemo(() => {
    const byDate = new Map<string, PidHistoryChartRow>();
    for (const row of adSpendChartData) {
      byDate.set(row.date, row);
    }
    return byDate;
  }, [adSpendChartData]);

  const [visibleSeries, setVisibleSeries] = useState<Record<ChartSeriesKey, boolean>>(() => ({
    pidActualDailyAdSpend: false,
    bqActualDailyAdSpend: true,
    bidHistoryTarget: true,
    targetDailyAdSpend: false,
    actualNewTargetRoas: false,
    clampedNewTargetRoas: false,
  }));

  const renderPidHistoryTooltip = useCallback(
    (props: TooltipContentProps<number, string>) => {
      const { active, payload, label } = props;
      if (!active || !payload?.length) {
        return null;
      }

      const row =
        typeof label === 'string'
          ? chartRowByDate.get(label)
          : typeof label === 'number'
            ? chartRowByDate.get(`${label}`)
            : undefined;
      if (!row) {
        return null;
      }

      const formatMoney = (value: number | null) =>
        value === null ? '-' : `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
      const formatNumber = (value: number | null) =>
        value === null ? '-' : value.toLocaleString(undefined, { maximumFractionDigits: 6 });
      const formatMaybeNumber = (value: number | null) =>
        value === null ? '-' : value.toLocaleString(undefined, { maximumFractionDigits: 6 });

      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {label ? `Date: ${String(label)}` : 'Details'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {row.createdAt}
          </Typography>

          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>PID kp:</strong> {formatNumber(row.pidKp)}
            </Typography>
            <Typography variant="body2">
              <strong>PID ki:</strong> {formatNumber(row.pidKi)}
            </Typography>
            <Typography variant="body2">
              <strong>PID kd:</strong> {formatNumber(row.pidKd)}
            </Typography>
            <Typography variant="body2">
              <strong>PID - Target daily ad spend:</strong> {formatMoney(row.targetDailyAdSpend)}
            </Typography>
            <Typography variant="body2">
              <strong>PID - Actual daily ad spend:</strong> {formatMoney(row.pidActualDailyAdSpend)}
            </Typography>
            <Typography variant="body2">
              <strong>BQ - Actual daily ad spend:</strong> {formatMoney(row.bqActualDailyAdSpend)}
            </Typography>
            <Typography variant="body2">
              <strong>Bid History - tROAS:</strong> {formatMaybeNumber(row.bidHistoryTarget)}
            </Typography>
            <Typography variant="body2">
              <strong>Bid History - Controller:</strong> {row.bidHistoryController ?? '-'}
            </Typography>
            <Typography variant="body2">
              <strong>PID - Actual new target ROAS:</strong> {formatMaybeNumber(row.actualNewTargetRoas)}
            </Typography>
            <Typography variant="body2">
              <strong>PID - Clamped new target ROAS:</strong> {formatMaybeNumber(row.clampedNewTargetRoas)}
            </Typography>
          </Stack>
        </Paper>
      );
    },
    [chartRowByDate],
  );

  const renderAxisNames = useCallback((props: unknown) => {
    const { width, height } = (props ?? {}) as { width?: number; height?: number };

    if (!width || !height) {
      return null;
    }

    const y = height - 8;
    return (
      <>
        <text x={8} y={y} fill="rgba(0,0,0,0.6)" fontSize={12}>
          Spend
        </text>
        <text x={width - 8} y={y} fill="rgba(0,0,0,0.6)" fontSize={12} textAnchor="end">
          ROAS
        </text>
      </>
    );
  }, []);

  return (
    <Paper sx={{ my: 4 }} id="ad-spend-history" className="branded-panel">
      <PageSubheader title="Ad Group Chart" />
      <Box px={2} pb={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <DateRangePicker
            localeText={{ start: 'Start date', end: 'End date' }}
            format="YYYY-MM-DD"
            value={dateRange}
            maxDate={toDayJs(todayUtc)}
            onChange={(newValue) => {
              const [nextStart, nextEnd] = newValue;
              if (nextStart) {
                setStartDate(nextStart.format('YYYY-MM-DD'));
              }
              if (nextEnd) {
                setEndDate(nextEnd.format('YYYY-MM-DD'));
              }
            }}
            slotProps={{
              textField: { size: 'small' },
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setEndDate(todayUtc);
                setStartDate(addDaysUtc(todayUtc, -6));
              }}
            >
              Last 1 week
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setEndDate(todayUtc);
                setStartDate(addDaysUtc(todayUtc, -13));
              }}
            >
              Last 2 weeks
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setEndDate(todayUtc);
                setStartDate(addDaysUtc(todayUtc, -29));
              }}
            >
              Last 1 month
            </Button>
          </Stack>
        </Stack>

        {isAdSpendHistoryLoading || isPidHistoryLoading || isBidHistoryLoading ? (
          <Box p={2} display="flex" justifyContent="center">
            <CircularProgress variant="indeterminate" />
          </Box>
        ) : adSpendHistoryError || pidHistoryError || bidHistoryError ? (
          <Alert severity="error">Failed to load ad spend history</Alert>
        ) : adSpendChartData.length === 0 ? (
          <Typography color="text.secondary">No ad spend history available</Typography>
        ) : (
          <>
            <Box sx={{ mb: 1 }}>
              <FormGroup>
                <Grid container spacing={1}>
                  {CHART_SERIES.map((series) => (
                    <Grid key={series.key} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={visibleSeries[series.key]}
                            onChange={(event) =>
                              setVisibleSeries((prev) => ({ ...prev, [series.key]: event.target.checked }))
                            }
                            sx={{
                              color: series.color,
                              '&.Mui-checked': { color: series.color },
                            }}
                          />
                        }
                        label={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 12, height: 12, bgcolor: series.color, borderRadius: 0.5 }} />
                            <Typography variant="body2">{series.label}</Typography>
                          </Stack>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </Box>

            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={adSpendChartData} margin={{ top: 5, right: 30, left: 0, bottom: 28 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(value) => String(value).slice(0, 10)} />
                <YAxis
                  yAxisId="spend"
                  orientation="left"
                  width={90}
                  tickFormatter={(value) => `$${Number(value).toLocaleString()}`}
                />
                <YAxis
                  yAxisId="roas"
                  orientation="right"
                  width={90}
                  tickFormatter={(value) => Number(value).toLocaleString()}
                />
                <Tooltip content={renderPidHistoryTooltip} />
                <Customized component={renderAxisNames} />

                {visibleSeries.pidActualDailyAdSpend && (
                  <Line
                    type="monotone"
                    yAxisId="spend"
                    dataKey="pidActualDailyAdSpend"
                    name="PID - Actual daily ad spend"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                )}
                {visibleSeries.bqActualDailyAdSpend && (
                  <Line
                    type="monotone"
                    yAxisId="spend"
                    dataKey="bqActualDailyAdSpend"
                    name="BQ - Actual daily ad spend"
                    stroke="#00897b"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                )}
                {visibleSeries.bidHistoryTarget && (
                  <Line
                    type="monotone"
                    yAxisId="roas"
                    dataKey="bidHistoryTarget"
                    name="Bid History - tROAS"
                    stroke="#6d4c41"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                )}
                {visibleSeries.targetDailyAdSpend && (
                  <Line
                    type="monotone"
                    yAxisId="spend"
                    dataKey="targetDailyAdSpend"
                    name="PID - Target daily ad spend"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="6 4"
                    connectNulls
                  />
                )}
                {visibleSeries.actualNewTargetRoas && (
                  <Line
                    type="monotone"
                    yAxisId="roas"
                    dataKey="actualNewTargetRoas"
                    name="PID - Actual new target ROAS"
                    stroke="#9c27b0"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                )}
                {visibleSeries.clampedNewTargetRoas && (
                  <Line
                    type="monotone"
                    yAxisId="roas"
                    dataKey="clampedNewTargetRoas"
                    name="PID - Clamped new target ROAS"
                    stroke="#ed6c02"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </Box>
    </Paper>
  );
}
